/**
 * useRouterSync - Syncs Zustand currentPage state with browser URL
 * 
 * This is a bridge between Zustand's page state and react-router-dom.
 * It enables:
 * - URL updates when navigating via setCurrentPage
 * - Deep linking support (e.g., /editor, /admin)
 * - Browser back/forward button support
 * - Page refresh without losing current page
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useStore from '../store/useStore';

// Map page names to URL paths
const PAGE_TO_PATH = {
  'landing': '/',
  'closet-type': '/closet-type',
  'room-setup': '/room-setup',
  'editor': '/editor',
  'review': '/review',
  'saved-designs': '/saved-designs',
  'booking': '/booking',
  'admin': '/admin',
  'super-admin': '/super-admin',
  'viewer-3d': '/viewer-3d',
  'mobile-generate': '/mobile-generate',
  'mobile-choice': '/mobile-choice',
  'my-account': '/my-account',
  'notifications': '/notifications',
};

// Map URL paths to page names
const PATH_TO_PAGE = Object.fromEntries(
  Object.entries(PAGE_TO_PATH).map(([page, path]) => [path, page])
);

export const useRouterSync = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = useStore((state) => state.currentPage);
  const setCurrentPage = useStore((state) => state.setCurrentPage);
  
  // Track if we're syncing to prevent loops
  const isSyncing = useRef(false);
  const lastSyncedPath = useRef(location.pathname);
  
  // Effect 1: Sync URL when Zustand page changes
  useEffect(() => {
    if (isSyncing.current) return;
    
    const targetPath = PAGE_TO_PATH[currentPage] || '/';
    
    // Don't navigate if we're already at the right path
    if (location.pathname === targetPath) {
      lastSyncedPath.current = targetPath;
      return;
    }
    
    // Don't override special routes that have their own components
    if (location.pathname === '/verify-email' || location.pathname === '/print-preview') {
      return;
    }
    
    isSyncing.current = true;
    navigate(targetPath, { replace: false });
    lastSyncedPath.current = targetPath;
    
    // Reset sync flag after navigation
    setTimeout(() => {
      isSyncing.current = false;
    }, 0);
  }, [currentPage, navigate, location.pathname]);
  
  // Effect 2: Sync Zustand when URL changes (browser back/forward, deep link)
  useEffect(() => {
    if (isSyncing.current) return;
    
    // Skip special routes
    if (location.pathname === '/verify-email' || location.pathname === '/print-preview') {
      return;
    }
    
    // If path changed from browser navigation
    if (location.pathname !== lastSyncedPath.current) {
      const newPage = PATH_TO_PAGE[location.pathname];
      
      if (newPage && newPage !== currentPage) {
        isSyncing.current = true;
        setCurrentPage(newPage);
        lastSyncedPath.current = location.pathname;
        
        setTimeout(() => {
          isSyncing.current = false;
        }, 0);
      }
    }
  }, [location.pathname, currentPage, setCurrentPage]);
  
  // Effect 3: Handle initial deep link on first load
  useEffect(() => {
    // Skip special routes
    if (location.pathname === '/verify-email' || location.pathname === '/print-preview') {
      return;
    }
    
    const pageFromUrl = PATH_TO_PAGE[location.pathname];
    
    // If we have a valid page from URL and it differs from current state
    if (pageFromUrl && pageFromUrl !== currentPage) {
      // Check if this page requires a project
      const projectRequiredPages = ['editor', 'viewer-3d', 'review', 'mobile-generate'];
      const project = useStore.getState().project;
      
      if (projectRequiredPages.includes(pageFromUrl) && !project) {
        // Redirect to landing if project required but not loaded
        setCurrentPage('landing');
        navigate('/', { replace: true });
      } else {
        setCurrentPage(pageFromUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount
};

export { PAGE_TO_PATH, PATH_TO_PAGE };
